import React, { useEffect, useState } from 'react'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import ActionButton from '@jimmyboys/liita-ui/dist/components/ActionButton'
import HFDropdown from 'components/HFInputs/HFDropdown'

import { useHistory } from 'react-router-dom'
import { zodResolver } from '@hookform/resolvers/zod'
import { FormProvider, useForm } from 'react-hook-form'
import { API_URL, fetchOptions, parseJSONContent } from 'store/fetchUtils'
import FinalizeTaskDialog from '../FinalizeTaskController/FinalizeTaskDialog'
import { ForwardTaskSchema } from './ForwardTaskSchema'
import { flatHookFormErrors } from '../utils'

import styles from '../Dialog.module.sass'
import { useFetchList } from 'store'
import { Options } from 'models'
import AlertBox from 'components/AlertBox/AlertBox'
import AmplitudeTaskParameters from 'models/Amplitude/AmplitudeTaskParameters'
import { useAnalytics } from 'hooks/useAnalytics'

const ForwardTaskController = (props) => {
	const { i18n } = useGlobal()
	const { data, resolve, dismiss, id } = props
	const [error, setError] = useState(null)
	const [defaultValues, setDefaultValues] = useState(null)
	const [submitting, setSubmitting] = useState(null)
	const history = useHistory()
	const { dispatchAnalyticsEvent } = useAnalytics()

	const {
		state,
		contents: [users],
	} = useFetchList(
		[`metadata/task_eligible_users`],
		[Options.fromSimpleObjectJSON]
	)

	users?.sort((a, b) => a.text.localeCompare(b.text))

	const methods = useForm({
		mode: 'onSubmit',
		resolver: zodResolver(ForwardTaskSchema),
		shouldUnregister: true,
	})
	const { handleSubmit, formState, reset } = methods
	const isLoading = state === 'loading'

	useEffect(() => {
		if (
			!isLoading &&
			defaultValues &&
			!formState.isDirty &&
			formState.submitCount === 0
		) {
			reset && reset(defaultValues)
		}
	}, [
		reset,
		defaultValues,
		isLoading,
		formState.isDirty,
		formState.submitCount,
	])

	useEffect(() => {
		if (!isLoading && users.length) {
			setDefaultValues({ sendTo: users[0].value })
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoading, users?.length])

	useEffect(() => {
		const flattenErrors = flatHookFormErrors(formState?.errors)

		if (flattenErrors) {
			const errorMessages = Object.keys(flattenErrors).map((errorKey) => {
				const errorMessage = flattenErrors[errorKey]
				const splittedErrorKey = errorKey.split('.')
				const field = splittedErrorKey[splittedErrorKey.length - 2]

				return `${i18n.translate(field)}: ${i18n.translate(
					errorMessage
				)}`
			})
			setError(errorMessages)
		}
	}, [formState?.errors, i18n])

	const onSubmit = ({ userId }) => {
		if (submitting) {
			return
		}

		setError(null)
		setSubmitting('submitting')

		fetch(
			`${API_URL}/api/tasks/${data.id}/transfer/`,
			fetchOptions({
				method: 'PUT',
				body: { userId },
			})
		)
			.then(async (r) => {
				if (r.ok) {
					setSubmitting('success')
					resolve()
					dispatchAnalyticsEvent(
						'forwardTask',
						AmplitudeTaskParameters.fromJSON(data)
					)
				} else {
					setError([(await parseJSONContent(r)).message])
				}
			})
			.catch((e) => {
				setSubmitting(false)
				setError([e.message || i18n`An error occur. Try again later.`])
			})
	}

	const customError = error?.map((err) => {
		return {
			title: i18n`Error`,
			text: err,
		}
	})

	return (
		<>
			{submitting && (
				<FinalizeTaskDialog
					dismiss={dismiss}
					submitting={submitting}
					task={data}
					submissionType='forward'
				/>
			)}
			<FormProvider {...methods}>
				<div className={styles['form-container']}>
					<form
						id={`form_forward_${id}`}
						className={styles['form']}
						onSubmit={handleSubmit(onSubmit)}
					>
						{customError && customError.length > 0 && (
							<AlertBox
								className={styles['alert-box']}
								type='error'
								items={customError}
							/>
						)}
						<h4
							className={`${styles['form-title']}`}
						>{i18n`Forward requisition`}</h4>
						<fieldset>
							<h5
								className={styles['input-title']}
							>{i18n`Send To?`}</h5>
							<HFDropdown
								name='userId'
								options={users || []}
								searchable={true}
								optionsAlign='right'
								noData={i18n`No results`}
							/>
						</fieldset>
					</form>
				</div>
			</FormProvider>
			<menu className={styles['menu']}>
				<ActionButton
					key='cancel'
					secondary={true}
					className={styles['menu-button-secondary']}
					onClick={() => {
						history.goBack()
					}}
				>
					{i18n`Cancel`}
				</ActionButton>
				<ActionButton
					className={styles['menu-button-primary-alternate']}
					form={`form_forward_${id}`}
				>
					{i18n`Forward`}
				</ActionButton>
			</menu>
		</>
	)
}

export default ForwardTaskController
